import { Link, NavLink } from '@remix-run/react';
import {
    Flex,
    Grid,
    useStyles,
    Button,
    pxToRem,
    SIZE_TO_HEIGHT_MAPPING,
} from '@archipro-design/aria';
import { useLocalisation } from '@archipro-website/localisation';
import * as S from './FeaturedSection.style';
import SectionHeader from '../section-header/SectionHeader';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import type { FeaturedSectionProps } from './FeaturedSection';
import { NarrowContent } from '@modules/articles/component/page-layout/PageLayout.style';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';
import { useTileHotButtons } from '~/modules/root';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

const FeaturedSectionDesktop: React.FC<FeaturedSectionProps> = ({
    featuredSection,
    showFeaturedSlides,
}) => {
    const {
        featureMain,
        topArticles,
        title,
        link,
        description,
        featuredSlides,
    } = featuredSection;

    const { css } = useStyles();
    const { handleFollow, isInDesignBoard } = useHandleFollow();

    const articleIDs = topArticles.map((article) => article.ID);
    articleIDs.push(featureMain.ID);

    const {
        ID,
        professionalID: _professionalID,
        bmEditorLink: _editorLink,
        image,
        title: articleTitle,
        date: featureMainDate,
        homePinned: _featuredMainHomePinned,
        ...featureMainRest
    } = featureMain;

    const { getTileTopActions, hotButtonModal } = useTileHotButtons({
        itemType: 'article',
        itemIDs: articleIDs,
    });

    const { localiseDate } = useLocalisation();

    return (
        <div>
            <Flex className={css(S.Header)}>
                <SectionHeader title={title} content={description} />
            </Flex>
            <div className={css(NarrowContent)}>
                <Flex className={css(S.Container)}>
                    <Flex className={css(S.StickyWrap)}>
                        {featureMain && (
                            <ArchizeenTile
                                {...featureMainRest}
                                as={Link}
                                to={featureMain.link}
                                prefetch="intent"
                                onFavourite={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleFollow({ id: ID }, 'ArticlePin');
                                }}
                                isFavourited={isInDesignBoard(ID, 'ArticlePin')}
                                className={css(S.MainTile)}
                                variables={{
                                    ...featureMainRest.variables,
                                    tilePadding: pxToRem(10),
                                }}
                                date={getFormattedLocalisedDate(
                                    localiseDate,
                                    featureMainDate
                                )}
                                image={{
                                    width: 700,
                                    height: 700,
                                    src: image,
                                    alt: articleTitle || title,
                                    sizes: getImagesSizes(700),
                                }}
                                title={articleTitle}
                                impression={{
                                    professionalID: featureMain.professionalID,
                                    itemID: ID,
                                    type: 'LibraryItem_Directory',
                                }}
                                topActions={getTileTopActions({
                                    item: featureMain,
                                })}
                                articleId={ID}
                            />
                        )}
                    </Flex>
                    <Flex className={css(S.MainWrap)} column={true}>
                        <Grid columns={2} className={css(S.TopArticleGrid)}>
                            {topArticles.map((article) => {
                                const {
                                    link,
                                    ID,
                                    professionalID,
                                    variant,
                                    size,
                                    canEdit: _canEdit,
                                    bmEditorLink: _bmEditorLink,
                                    date,
                                    homePinned: _homePinned,
                                    ...rest
                                } = article;

                                const tileSize =
                                    SIZE_TO_HEIGHT_MAPPING[variant];
                                const height =
                                    size === 'small' && 'small' in tileSize
                                        ? tileSize.small
                                        : 'medium' in tileSize
                                          ? tileSize.medium
                                          : tileSize.large;

                                return (
                                    <ArchizeenTile
                                        key={ID}
                                        as={Link}
                                        to={link}
                                        prefetch="intent"
                                        variant={variant}
                                        size={size}
                                        date={getFormattedLocalisedDate(
                                            localiseDate,
                                            date
                                        )}
                                        {...rest}
                                        onFavourite={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleFollow(
                                                { id: ID },
                                                'ArticlePin'
                                            );
                                        }}
                                        isFavourited={isInDesignBoard(
                                            ID,
                                            'ArticlePin'
                                        )}
                                        variables={{
                                            ...article.variables,
                                            tilePadding: pxToRem(10),
                                        }}
                                        image={{
                                            width: 340,
                                            height: height,
                                            src: article.image,
                                            alt: article.title || '',
                                            sizes: getImagesSizes(340),
                                        }}
                                        impression={{
                                            professionalID: professionalID,
                                            itemID: ID,
                                            type: 'LibraryItem_Directory',
                                        }}
                                        topActions={getTileTopActions({
                                            item: article,
                                        })}
                                        articleId={ID}
                                    />
                                );
                            })}
                        </Grid>
                        <Button
                            className={css(S.ViewMore)}
                            size={16}
                            as={NavLink}
                            to={link}
                            prefetch={'intent'}
                        >
                            VIEW MORE {title.toUpperCase()}
                        </Button>
                    </Flex>
                </Flex>
            </div>
            {showFeaturedSlides && featuredSlides.length > 0 ? (
                <Flex className={css(S.CarouselContainer)}>
                    <FeaturedCarousel featuredSlides={featuredSlides} />
                </Flex>
            ) : null}
            {hotButtonModal}
        </div>
    );
};

export default FeaturedSectionDesktop;
